import type { EnginConsommationRow } from '@/types';
import { Card, DataTable } from '@/components/ui';

function formatNum(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : '0';
}

function ConsommationCell({ row }: { row: EnginConsommationRow }) {
  const totalH = row.total_h ?? 0;
  const totalKm = row.total_km ?? 0;
  const totalGasoil = row.total_gasoil ?? 0;
  const value = Number(row.engin?.current_consommation) ?? 0;
  const max = Number(row.engin?.consommation_max) ?? 0;
  const min = Number(row.engin?.consommation_min) ?? 0;
  let computed = 0;
  if (totalH !== 0) computed = totalGasoil / totalH;
  else if (totalKm !== 0) computed = (totalGasoil * 100) / totalKm;
  const display = computed > 0 ? computed.toFixed(2) : value.toFixed(2);
  let color = 'text-slate-800';
  if (max > 0 && value >= max) color = 'text-red-600 font-semibold';
  else if (min > 0 && value <= min) color = 'text-green-600 font-semibold';
  return <span className={color}>{display}</span>;
}

export default function EnginConsommationTable({ data }: { data: EnginConsommationRow[] }) {
  const columns = [
    { key: 'engin.code', label: 'Engin', render: (_: unknown, row: EnginConsommationRow) => row.engin?.code ?? '—' },
    { key: 'engin.famille', label: 'Famille', render: (_: unknown, row: EnginConsommationRow) => (row.engin?.famille as { nom?: string })?.nom ?? '—' },
    { key: 'engin.current_cpt', label: 'Cpt', render: (_: unknown, row: EnginConsommationRow) => formatNum(Number(row.engin?.current_cpt)) },
    { key: 'consommation', label: 'Consommation', render: (_: unknown, row: EnginConsommationRow) => <ConsommationCell row={row} /> },
    { key: 'total_km', label: 'Cumul KM', render: (v: unknown) => formatNum(Number(v)) },
    { key: 'total_h', label: 'Cumul H', render: (v: unknown) => formatNum(Number(v)) },
    { key: 'total_gasoil', label: 'Total gasoil', render: (v: unknown) => formatNum(Number(v)) },
  ];

  const dataWithKey = data.map((row) => ({ ...row, _key: String(row.engin?.id ?? '') }));

  return (
    <Card>
      <DataTable<EnginConsommationRow & { _key?: string } & Record<string, unknown>>
        columns={columns}
        data={dataWithKey}
        keyField="_key"
        emptyMessage="Aucune donnée sur la période"
      />
    </Card>
  );
}
